import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { registerUser } from "../../features/user/UserSlice";
import { AppDispatch, RootState } from "../../store";
import { toast } from "sonner";
import { FiEye, FiEyeOff, FiCheck, FiX } from "react-icons/fi";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 6;

// Dark-theme styling for react-phone-number-input's inner elements
const phoneWrapperClass =
  "flex w-full items-center rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 transition " +
  "focus-within:border-web-primary/60 focus-within:ring-1 focus-within:ring-web-primary/40 " +
  "[&_.PhoneInput]:w-full [&_.PhoneInput]:gap-2 " +
  "[&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:border-none [&_.PhoneInputInput]:outline-none " +
  "[&_.PhoneInputInput]:text-sm [&_.PhoneInputInput]:text-white [&_.PhoneInputInput]:placeholder:text-white/25 " +
  "[&_.PhoneInputCountrySelect]:bg-[#0d1a0f] [&_.PhoneInputCountrySelect]:text-white " +
  "[&_.PhoneInputCountrySelectArrow]:text-white/50 [&_.PhoneInputCountrySelectArrow]:opacity-100";

type FormState = {
  fullName: string;
  mobile: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const inputClass =
  "w-full rounded-lg bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/25 " +
  "focus:outline-none focus:border-web-primary/60 focus:ring-1 focus:ring-web-primary/40 disabled:opacity-50 transition";

const labelClass = "block text-xs font-medium uppercase tracking-widest text-white";

export default function Register() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isLoading } = useSelector((state: RootState) => state.user);

  const [form, setForm] = useState<FormState>({
    fullName: "",
    mobile: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    let value = e.target.value;

    if (name === "fullName") {
      value = value.replace(/\s{2,}/g, " "); // allow single spaces in names
    } else {
      value = value.replace(/\s/g, ""); // no spaces in email / passwords
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const passwordsMatch =
    form.confirmPassword.length > 0 && form.password === form.confirmPassword;
  const showMismatch =
    form.confirmPassword.length > 0 && form.password !== form.confirmPassword;
  const passwordTooShort =
    form.password.length > 0 && form.password.length < MIN_PASSWORD;

  const validate = (): string | null => {
    if (
      !form.fullName.trim() ||
      !form.mobile ||
      !form.email ||
      !form.password ||
      !form.confirmPassword
    ) {
      return "Please fill in all fields";
    }
    if (form.fullName.trim().length < 2) return "Please enter your full name";
    if (!isValidPhoneNumber(form.mobile)) return "Enter a valid mobile number";
    if (!EMAIL_REGEX.test(form.email)) return "Enter a valid email address";
    if (form.password.length < MIN_PASSWORD)
      return `Password must be at least ${MIN_PASSWORD} characters`;
    if (form.password !== form.confirmPassword) return "Passwords do not match";
    return null;
  };

  const handleRegister = async (e?: React.FormEvent) => {
    e?.preventDefault();

    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    const { confirmPassword, ...payload } = form; // confirmPassword never sent to API
    const result = await dispatch(
      registerUser({ ...payload, fullName: payload.fullName.trim() })
    );

    if (registerUser.fulfilled.match(result)) {
      navigate("/login", { state: { emailOrMobile: payload.email } });
    }
    // rejected case: toast is shown by the slice
  };

  return (
    <section className="relative overflow-hidden bg-web-background min-h-screen flex items-center justify-center lg:px-24 md:px-12 px-4 py-6">
      <div className="absolute inset-0 bg-web-radial-glow" />
      <div className="absolute left-1/2 top-1/2 h-[40vw] w-[40vw] min-h-[250px] min-w-[250px] max-h-[500px] max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-web-primary/20 blur-[140px]" />

      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        {/* Header: compact so everything fits on one screen */}
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/10 shadow shadow-web-radial-glow">
            <img src="/images/lock.svg" alt="Lock" className="h-5 w-5" />
          </div>
          <h2 className="text-3xl font-bold leading-tight text-white md:text-4xl">
            Create <span className="text-web-primary">Account.</span>
          </h2>
        </div>

        <p className="mt-2 text-sm text-white/70 text-center">
          Fill in your details to get access to the console.
        </p>

        <form
          onSubmit={handleRegister}
          className="mt-6 w-full rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm px-6 py-6 md:px-8 shadow-2xl"
        >
          <div className="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
            {/* Full name */}
            <div className="space-y-1.5">
              <label className={labelClass} htmlFor="fullName">Full Name</label>
              <input
                id="fullName"
                type="text"
                name="fullName"
                placeholder="John Doe"
                autoComplete="name"
                value={form.fullName}
                onChange={handleChange}
                disabled={isLoading}
                className={inputClass}
              />
            </div>

            {/* Mobile */}
            <div className="space-y-1.5">
              <label className={labelClass} htmlFor="mobile">Mobile</label>
              <div className={`${phoneWrapperClass} ${isLoading ? "opacity-50" : ""}`}>
                <PhoneInput
                  id="mobile"
                  defaultCountry="IN"
                  international
                  withCountryCallingCode
                  countryCallingCodeEditable={false}
                  autoComplete="tel"
                  placeholder="Enter mobile number"
                  value={form.mobile}
                  // value is E.164, e.g. "+918727878327"
                  onChange={(value) => setForm((prev) => ({ ...prev, mobile: value ?? "" }))}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
            </div>

            {/* Email: full width */}
            <div className="space-y-1.5 md:col-span-2">
              <label className={labelClass} htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="john@company.com"
                autoComplete="email"
                value={form.email}
                onChange={handleChange}
                disabled={isLoading}
                className={inputClass}
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className={labelClass} htmlFor="password">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  className={`${inputClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition"
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div className="space-y-1.5">
              <label className={labelClass} htmlFor="confirmPassword">Confirm Password</label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  onPaste={(e) => e.preventDefault()} // force retyping
                  disabled={isLoading}
                  className={`${inputClass} pr-11 ${
                    showMismatch
                      ? "border-red-500/60 focus:border-red-500/60 focus:ring-red-500/40"
                      : passwordsMatch
                      ? "border-web-primary/60"
                      : ""
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((p) => !p)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition"
                >
                  {showConfirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            {/* Password hints: one fixed-height row so the layout doesn't jump */}
            <div className="md:col-span-2 -mt-2 min-h-[16px] text-xs">
              {passwordTooShort ? (
                <p className="text-white/40">Password must be at least {MIN_PASSWORD} characters</p>
              ) : showMismatch ? (
                <p className="flex items-center gap-1 text-red-400">
                  <FiX size={12} /> Passwords do not match
                </p>
              ) : passwordsMatch ? (
                <p className="flex items-center gap-1 text-web-primary">
                  <FiCheck size={12} /> Passwords match
                </p>
              ) : null}
            </div>

            {/* Submit: full width */}
            <button
              type="submit"
              disabled={isLoading || showMismatch}
              className="md:col-span-2 w-full rounded-lg bg-web-primary py-3 text-sm font-semibold text-[#0d1a0f]
                hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Creating account…" : "Create Account"}
            </button>
          </div>

          <p className="mt-5 text-center text-sm text-white">
            Already have an account?{" "}
            <Link to="/login" className="text-web-primary hover:text-web-primary/80 transition">
              Sign In
            </Link>
          </p>
        </form>
      </div>
    </section>
  );
}